import React, { useState, useEffect } from "react";
// all imports
import { getUserById, updateUserProfile } from "../../services/userService.js";
import DashboardLayout from "../../layout/DashboardLayout.js";
import LogoSearchHeader from "../../components/LogoSearchHeader.js";
import PaymentStatus from "../../components/PaymentStatus.js";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Button,
    Typography,
    Box,
    Alert,
} from "@mui/material";

function Dashboard() {
    const [userData, setUserData] = useState(null);
    const [missingDataDialog, setMissingDataDialog] = useState(false);
    const [formData, setFormData] = useState({
        email: '',
        firstName: '',
        lastName: '',
        company: '',
        phone: ''
    });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    // Check for missing user data on component mount
    useEffect(() => {
        const fetchData = async () => {
            try {
                // Check if userInfo exists in localStorage
                const userInfo = localStorage.getItem('userInfo');        
                if (!userInfo) {
                    window.location.href = '/login';
                    return;
                }
                // Parse the userInfo
                let user;
                try {
                    user = JSON.parse(userInfo);
                    setUserData(user);
                    // Validate that user has required fields
                    if (!user || !user._id) {
                        localStorage.removeItem('userInfo');
                        window.location.href = '/login';
                        return;
                    }
                } catch (parseError) {
                    localStorage.removeItem('userInfo');
                    window.location.href = '/login';
                    return;
                }
                // Check if required fields are missing for profile completion
                if (!user.firstName || !user.lastName || !user.company || !user.phone) {
                    setMissingDataDialog(true);
                    setFormData({
                        email: user.email || '',
                        firstName: user.firstName || '',
                        lastName: user.lastName || '',
                        company: user.company || '',
                        phone: user.phone || ''
                    });
                }
            } catch (error) {
                console.error('Error in fetchData:', error);
                // If there's an error fetching user data, redirect to login
                localStorage.removeItem('userInfo');
                window.location.href = '/login';
            }
        };
        fetchData();
    }, []);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSubmitMissingData = async () => {
        setLoading(true);
        setError(null);
        try {
            // Validate required fields
            if (!formData.email.trim() || !formData.firstName.trim() || !formData.lastName.trim() || !formData.company.trim() || !formData.phone.trim()) {
                setError('Please fill in all required fields');
                return;
            }
            // Prepare profile data for backend update
            const profileUpdateData = {
                email: formData.email.trim(),
                firstName: formData.firstName.trim(),
                lastName: formData.lastName.trim(),
                company: formData.company.trim(),
                phone: formData.phone.trim()
            };
            // Update user data in backend
            const updatedUserResponse = await updateUserProfile(userData._id, profileUpdateData);

            // Update user data in localStorage with response from backend
            const updatedUserData = {
                ...userData,
                ...profileUpdateData
            };

            localStorage.setItem('userInfo', JSON.stringify(updatedUserData));
            setUserData(updatedUserData);
            setMissingDataDialog(false);
        } catch (error) {
            console.error('Error updating user data:', error);
            setError(error.response?.data?.message || 'Failed to update user data. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const handleCloseDialog = () => {
        if (formData.email.trim() && formData.firstName.trim() && formData.lastName.trim() && formData.company.trim() && formData.phone.trim()) {
            setMissingDataDialog(false);
        } else {
            setError('Please fill in all required fields before closing');
        }
    };
    return (
        <DashboardLayout pageName="Dashboard">
            {/* Payment Status Display */}
            <PaymentStatus />
            {/* Payment Status Display */}
            
            {/* Display Area Starts */}
            <LogoSearchHeader />
            {/* Display Area Starts */}
            
            {/* <SearcheResults onCardClick={handleCardClick} /> */}
            {/* Display Area Ends */}

            {/* Missing Data Dialog */}
            <Dialog open={missingDataDialog}  onClose={handleCloseDialog} maxWidth="sm" fullWidth disableEscapeKeyDown disableBackdropClick >
              <DialogTitle>
                <Typography variant="h5" sx={{ fontWeight: 600 }}>Complete Your Profile</Typography>
              </DialogTitle>
              <DialogContent>
                <Box sx={{ pt: 1, pb: 1 }}>
                  <Typography variant="body2" sx={{ mb: 2, color: '#666' }}>Please provide the following information to complete your profile setup.</Typography>
                  {error && (<Alert severity="error" sx={{ mb: 1 }} onClose={() => setError(null)}> {error} </Alert> )}

                  <TextField autoFocus margin="dense" name="email" label="Email" type="email" fullWidth variant="outlined" value={formData.email} onChange={handleInputChange} sx={{ mb: 1 }} required disabled />

                  <TextField margin="dense" name="firstName" label="First Name" type="text" fullWidth variant="outlined" value={formData.firstName} onChange={handleInputChange} sx={{ mb: 1 }} required />
            
                  <TextField margin="dense" name="lastName" label="Last Name" type="text" fullWidth variant="outlined" value={formData.lastName} onChange={handleInputChange} sx={{ mb: 1 }} required />
            
                  <TextField margin="dense" name="company" label="Company" type="text" fullWidth variant="outlined" value={formData.company} onChange={handleInputChange} sx={{ mb: 1 }} required />

                  <TextField margin="dense" name="phone" label="Phone" type="text" fullWidth variant="outlined" value={formData.phone} onChange={handleInputChange} sx={{ mb: 1 }} required />
                </Box>
              </DialogContent>
              <DialogActions sx={{ p: 3, pt: 1 }}>
                <Button 
                  onClick={handleSubmitMissingData} 
                  variant="contained" 
                  disabled={loading || !formData.email.trim() || !formData.firstName.trim() || !formData.lastName.trim() || !formData.company.trim() || !formData.phone.trim() }
                  sx={{ 
                    bgcolor: '#1ce484',
                    '&:hover': { bgcolor: '#20ec84' },
                    '&:disabled': { bgcolor: '#ccc' }
                  }}
                >
                  {loading ? 'Updating...' : 'Update Profile'}
                </Button>
              </DialogActions>
            </Dialog>
    </DashboardLayout>
    
 );
}

export default Dashboard;